import { z } from "zod";
import { getZodSchemaFromClass, SensorElementConfigKeyString, validate } from "../../zod-decorator/index.js";
import { VertexInfo } from "../commonSubSchemas/vertexInfo.js";
import { sensorElementConfigs } from "../../Configurations/SensorConfigurations/sensorElements/sensorElementConfigs.js";
import { SensorElementAddress } from "../commonSubSchemas/sensorElementAddress.js";
import { SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type { SensorElementFieldsWithDefaults, SensorElementSchemaTypes } from "../types/sensorElement.d.js";
import { type DtoValidatorTypes } from "../validatableClass.js";

export type SensorElementConfigKey = keyof typeof sensorElementConfigs;

const SCHEMA_KEY = "SensorElement" as const;

/**
 * Represents one measurement channel produced by a SensorCluster.
 *
 * A SensorElement is the atomic unit of sensor data — it maps a single physical
 * measurement (e.g. flow rate, pressure) to the conversion formula and parameters
 * needed to turn a decoded device reading into a converted (user-visible) value.
 *
 * Elements are computed on demand from a SensorCluster via
 * `SensorCluster.getters.sensorElements(cluster)` and are not persisted.
 */
export class SensorElement extends SchemaBase<SensorElement, SensorElementFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    /**
     * Where this element lives on its host: host reference, port, channel, communication
     * mode, and sub-channel. Derived from the parent cluster's address by
     * `SensorCluster.getters.sensorElements`.
     */
    @validate({ childClass: SensorElementAddress })
    public address!: SensorElementAddress;

    /**
     * Key into `sensorElementConfigs` identifying which element type this record
     * represents. Determines the interpretation of `parameters` and the conversion
     * formula used to turn decoded values into converted values.
     */
    @validate({ zodValidator: SensorElementConfigKeyString })
    public sensorElementConfigKey!: SensorElementConfigKey;

    /**
     * Parameters for this element's conversion function. Set by the parent cluster via
     * `generateSensorElements` — never edited directly by users.
     */
    @validate({ zodValidator: z.record(z.string(), z.union([z.number(), z.string()])).optional() })
    public parameters: Record<string, number | string> = {};

    /**
     * Physical location of this sensor element. `depth` is below ground in meters
     * (positive = below surface; 0 = ground level). lat/lng match the cluster's
     * installation position unless the cluster config overrides them per element.
     * Populated by `SensorCluster.getters.sensorElements`; absent when the cluster
     * has no position information.
     */
    @validate({ childClass: VertexInfo })
    public position!: VertexInfo;

    static getters = {
        validateParameters: (self: SensorElement): [boolean, string] => {
            const config = sensorElementConfigs[self.sensorElementConfigKey];
            if (!config) return [false, `Unknown element config key: "${self.sensorElementConfigKey}"`];
            const problems = Object.values(config.parameterConfigs).flatMap((paramConfig) => {
                const value = self.parameters[paramConfig.key];
                const [valid, message] = paramConfig.validator(value as any);
                return valid ? [] : [`${paramConfig.key}: ${message}`];
            });
            return problems.length === 0 ? [true, ""] : [false, problems.join("; ")];
        },
    } as const satisfies Record<string, (self: SensorElement) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<SensorElementSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<SensorElement, SensorElementFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

SensorElement.dtoValidators = getZodSchemaFromClass(
    SensorElement,
) as unknown as DtoValidatorTypes<SensorElementSchemaTypes>;
